import { validateGbaInputProfile, type GbaInputProfile } from '@retro-arcade/emulator';

export interface PersistenceIdentity {
  readonly gameId: string;
  readonly variantId: string;
  readonly runtimeId: string;
  readonly romSha256: string;
}

export interface StoredNativeSave {
  readonly schemaVersion: 1;
  readonly kind: 'NATIVE_SAVE';
  readonly key: string;
  readonly identity: PersistenceIdentity;
  readonly sizeBytes: number;
  readonly updatedAt: string;
  readonly data: ArrayBuffer;
}

export interface StoredSaveState {
  readonly schemaVersion: 1;
  readonly kind: 'SAVE_STATE';
  readonly key: string;
  readonly identity: PersistenceIdentity;
  readonly slot: number;
  readonly sizeBytes: number;
  readonly updatedAt: string;
  readonly data: ArrayBuffer;
}

export interface StoredGbaInputProfile {
  readonly schemaVersion: 1;
  readonly kind: 'GBA_INPUT_PROFILE';
  readonly key: string;
  readonly runtimeId: string;
  readonly controlScheme: 'gba';
  readonly profile: GbaInputProfile;
  readonly updatedAt: string;
}

export interface CheatEntry {
  readonly id: string;
  readonly description: string;
  readonly code: string;
  readonly enabled: boolean;
}

export interface StoredCheatProfile {
  readonly schemaVersion: 1;
  readonly kind: 'CHEAT_PROFILE';
  readonly key: string;
  readonly identity: PersistenceIdentity;
  readonly entries: readonly CheatEntry[];
  readonly updatedAt: string;
}

export interface PlayerStorageSummary {
  readonly nativeSaves: { readonly count: number; readonly totalBytes: number };
  readonly saveStates: { readonly count: number; readonly totalBytes: number };
  readonly inputProfiles: { readonly count: number };
  readonly cheatProfiles: { readonly count: number };
  readonly totalUserDataBytes: number;
}

const databaseName = 'retro-arcade-player-v1';
const databaseVersion = 3;
const nativeSaveStoreName = 'native-saves';
const saveStateStoreName = 'save-states';
const inputProfileStoreName = 'input-profiles';
const cheatProfileStoreName = 'cheat-profiles';
const sha256Pattern = /^[a-f0-9]{64}$/;
const maxNativeSaveBytes = 8 * 1024 * 1024;
const maxSaveStateBytes = 64 * 1024 * 1024;

function requireNonEmpty(value: string, field: string): string {
  const normalized = value.trim();
  if (!normalized) throw new Error(`PERSISTENCE_IDENTITY_INVALID: ${field}`);
  return normalized;
}

export function persistenceIdentityKey(identity: PersistenceIdentity): string {
  const runtimeId = requireNonEmpty(identity.runtimeId, 'runtimeId');
  const gameId = requireNonEmpty(identity.gameId, 'gameId');
  const variantId = requireNonEmpty(identity.variantId, 'variantId');
  if (!sha256Pattern.test(identity.romSha256)) {
    throw new Error('PERSISTENCE_IDENTITY_INVALID: romSha256');
  }

  return [
    encodeURIComponent(runtimeId),
    identity.romSha256,
    encodeURIComponent(gameId),
    encodeURIComponent(variantId),
  ].join('/');
}

export function nativeSaveKey(identity: PersistenceIdentity): string {
  return `native/${persistenceIdentityKey(identity)}`;
}

export function saveStateKey(identity: PersistenceIdentity, slot: number): string {
  if (!Number.isInteger(slot) || slot < 1 || slot > 9) {
    throw new RangeError('Save-state slot must be an integer between 1 and 9.');
  }
  return `state/${persistenceIdentityKey(identity)}/${slot}`;
}

export function gbaInputProfileKey(runtimeId: string): string {
  return `input/${encodeURIComponent(requireNonEmpty(runtimeId, 'runtimeId'))}/gba`;
}

export function cheatProfileKey(identity: PersistenceIdentity): string {
  return `cheats/${persistenceIdentityKey(identity)}`;
}

function normalizeCheatEntries(entries: readonly CheatEntry[]): CheatEntry[] {
  if (entries.length > 64) throw new Error('CHEAT_PROFILE_TOO_LARGE');

  const ids = new Set<string>();
  return entries.map((entry) => {
    const id = requireNonEmpty(entry.id, 'cheat.id');
    if (id.length > 80 || ids.has(id)) throw new Error('CHEAT_PROFILE_INVALID: id');
    ids.add(id);

    const description = requireNonEmpty(entry.description, 'cheat.description');
    if (description.length > 120) throw new Error('CHEAT_PROFILE_INVALID: description');

    const code = requireNonEmpty(entry.code, 'cheat.code');
    if (code.length > 4096) throw new Error('CHEAT_PROFILE_INVALID: code');

    if (typeof entry.enabled !== 'boolean') throw new Error('CHEAT_PROFILE_INVALID: enabled');

    return { id, description, code, enabled: entry.enabled };
  });
}

export function summarizePlayerStorageRecords(
  nativeSaves: readonly Pick<StoredNativeSave, 'sizeBytes'>[],
  saveStates: readonly Pick<StoredSaveState, 'sizeBytes'>[],
  inputProfiles: readonly unknown[],
  cheatProfiles: readonly unknown[],
): PlayerStorageSummary {
  const nativeSaveBytes = nativeSaves.reduce((total, record) => total + record.sizeBytes, 0);
  const saveStateBytes = saveStates.reduce((total, record) => total + record.sizeBytes, 0);

  return {
    nativeSaves: { count: nativeSaves.length, totalBytes: nativeSaveBytes },
    saveStates: { count: saveStates.length, totalBytes: saveStateBytes },
    inputProfiles: { count: inputProfiles.length },
    cheatProfiles: { count: cheatProfiles.length },
    totalUserDataBytes: nativeSaveBytes + saveStateBytes,
  };
}

function persistenceBuffer(
  data: Uint8Array,
  maxBytes: number,
  kind: 'native save' | 'save state',
): ArrayBuffer {
  if (!(data instanceof Uint8Array) || data.byteLength === 0) {
    throw new Error(`PERSISTENCE_PAYLOAD_INVALID: ${kind} is empty`);
  }
  if (data.byteLength > maxBytes) {
    throw new Error(`PERSISTENCE_PAYLOAD_TOO_LARGE: ${kind}`);
  }
  return data.slice().buffer;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('INDEXEDDB_REQUEST_FAILED'));
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () =>
      reject(transaction.error ?? new Error('INDEXEDDB_TRANSACTION_ABORTED'));
    transaction.onerror = () =>
      reject(transaction.error ?? new Error('INDEXEDDB_TRANSACTION_FAILED'));
  });
}

function openPersistenceDatabase(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('INDEXEDDB_UNAVAILABLE'));
  }

  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(databaseName, databaseVersion);
    request.onerror = () => reject(request.error ?? new Error('INDEXEDDB_OPEN_FAILED'));
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(nativeSaveStoreName)) {
        db.createObjectStore(nativeSaveStoreName, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(saveStateStoreName)) {
        db.createObjectStore(saveStateStoreName, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(inputProfileStoreName)) {
        db.createObjectStore(inputProfileStoreName, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(cheatProfileStoreName)) {
        db.createObjectStore(cheatProfileStoreName, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
  });
}

export class PlayerPersistenceStore {
  async inspectStorage(): Promise<PlayerStorageSummary> {
    const db = await openPersistenceDatabase();
    try {
      const transaction = db.transaction(
        [nativeSaveStoreName, saveStateStoreName, inputProfileStoreName, cheatProfileStoreName],
        'readonly',
      );
      const nativeRequest = transaction.objectStore(nativeSaveStoreName).getAll();
      const stateRequest = transaction.objectStore(saveStateStoreName).getAll();
      const inputRequest = transaction.objectStore(inputProfileStoreName).getAll();
      const cheatRequest = transaction.objectStore(cheatProfileStoreName).getAll();

      const [nativeSaves, saveStates, inputProfiles, cheatProfiles] = await Promise.all([
        requestResult<StoredNativeSave[]>(nativeRequest),
        requestResult<StoredSaveState[]>(stateRequest),
        requestResult<StoredGbaInputProfile[]>(inputRequest),
        requestResult<StoredCheatProfile[]>(cheatRequest),
      ]);
      await transactionComplete(transaction);

      return summarizePlayerStorageRecords(nativeSaves, saveStates, inputProfiles, cheatProfiles);
    } finally {
      db.close();
    }
  }

  async getNativeSave(identity: PersistenceIdentity): Promise<StoredNativeSave | null> {
    const db = await openPersistenceDatabase();
    try {
      const transaction = db.transaction(nativeSaveStoreName, 'readonly');
      const request = transaction.objectStore(nativeSaveStoreName).get(nativeSaveKey(identity));
      const result = await requestResult<StoredNativeSave | undefined>(request);
      await transactionComplete(transaction);
      return result ?? null;
    } finally {
      db.close();
    }
  }

  async putNativeSave(
    identity: PersistenceIdentity,
    data: Uint8Array,
    now = new Date(),
  ): Promise<StoredNativeSave> {
    const record: StoredNativeSave = {
      schemaVersion: 1,
      kind: 'NATIVE_SAVE',
      key: nativeSaveKey(identity),
      identity: { ...identity },
      sizeBytes: data.byteLength,
      updatedAt: now.toISOString(),
      data: persistenceBuffer(data, maxNativeSaveBytes, 'native save'),
    };

    const db = await openPersistenceDatabase();
    try {
      const transaction = db.transaction(nativeSaveStoreName, 'readwrite');
      transaction.objectStore(nativeSaveStoreName).put(record);
      await transactionComplete(transaction);
      return record;
    } finally {
      db.close();
    }
  }

  async deleteNativeSave(identity: PersistenceIdentity): Promise<void> {
    const db = await openPersistenceDatabase();
    try {
      const transaction = db.transaction(nativeSaveStoreName, 'readwrite');
      transaction.objectStore(nativeSaveStoreName).delete(nativeSaveKey(identity));
      await transactionComplete(transaction);
    } finally {
      db.close();
    }
  }

  async getSaveState(identity: PersistenceIdentity, slot: number): Promise<StoredSaveState | null> {
    const db = await openPersistenceDatabase();
    try {
      const transaction = db.transaction(saveStateStoreName, 'readonly');
      const request = transaction.objectStore(saveStateStoreName).get(saveStateKey(identity, slot));
      const result = await requestResult<StoredSaveState | undefined>(request);
      await transactionComplete(transaction);
      return result ?? null;
    } finally {
      db.close();
    }
  }

  async putSaveState(
    identity: PersistenceIdentity,
    slot: number,
    data: Uint8Array,
    now = new Date(),
  ): Promise<StoredSaveState> {
    const record: StoredSaveState = {
      schemaVersion: 1,
      kind: 'SAVE_STATE',
      key: saveStateKey(identity, slot),
      identity: { ...identity },
      slot,
      sizeBytes: data.byteLength,
      updatedAt: now.toISOString(),
      data: persistenceBuffer(data, maxSaveStateBytes, 'save state'),
    };

    const db = await openPersistenceDatabase();
    try {
      const transaction = db.transaction(saveStateStoreName, 'readwrite');
      transaction.objectStore(saveStateStoreName).put(record);
      await transactionComplete(transaction);
      return record;
    } finally {
      db.close();
    }
  }

  async listSaveStates(identity: PersistenceIdentity): Promise<StoredSaveState[]> {
    const db = await openPersistenceDatabase();
    try {
      const transaction = db.transaction(saveStateStoreName, 'readonly');
      const request = transaction.objectStore(saveStateStoreName).getAll();
      const records = await requestResult<StoredSaveState[]>(request);
      await transactionComplete(transaction);
      const prefix = `state/${persistenceIdentityKey(identity)}/`;
      return records
        .filter((record) => record.key.startsWith(prefix))
        .sort((left, right) => left.slot - right.slot);
    } finally {
      db.close();
    }
  }

  async deleteSaveState(identity: PersistenceIdentity, slot: number): Promise<void> {
    const db = await openPersistenceDatabase();
    try {
      const transaction = db.transaction(saveStateStoreName, 'readwrite');
      transaction.objectStore(saveStateStoreName).delete(saveStateKey(identity, slot));
      await transactionComplete(transaction);
    } finally {
      db.close();
    }
  }

  async getGbaInputProfile(runtimeId: string): Promise<StoredGbaInputProfile | null> {
    const db = await openPersistenceDatabase();
    try {
      const transaction = db.transaction(inputProfileStoreName, 'readonly');
      const request = transaction
        .objectStore(inputProfileStoreName)
        .get(gbaInputProfileKey(runtimeId));
      const result = await requestResult<StoredGbaInputProfile | undefined>(request);
      await transactionComplete(transaction);
      return result ?? null;
    } finally {
      db.close();
    }
  }

  async putGbaInputProfile(
    runtimeId: string,
    profile: GbaInputProfile,
    now = new Date(),
  ): Promise<StoredGbaInputProfile> {
    validateGbaInputProfile(profile);

    const record: StoredGbaInputProfile = {
      schemaVersion: 1,
      kind: 'GBA_INPUT_PROFILE',
      key: gbaInputProfileKey(runtimeId),
      runtimeId: requireNonEmpty(runtimeId, 'runtimeId'),
      controlScheme: 'gba',
      profile: {
        keyboard: { ...profile.keyboard },
        gamepad: { ...profile.gamepad },
      },
      updatedAt: now.toISOString(),
    };

    const db = await openPersistenceDatabase();
    try {
      const transaction = db.transaction(inputProfileStoreName, 'readwrite');
      transaction.objectStore(inputProfileStoreName).put(record);
      await transactionComplete(transaction);
      return record;
    } finally {
      db.close();
    }
  }

  async deleteGbaInputProfile(runtimeId: string): Promise<void> {
    const db = await openPersistenceDatabase();
    try {
      const transaction = db.transaction(inputProfileStoreName, 'readwrite');
      transaction.objectStore(inputProfileStoreName).delete(gbaInputProfileKey(runtimeId));
      await transactionComplete(transaction);
    } finally {
      db.close();
    }
  }

  async getCheatProfile(identity: PersistenceIdentity): Promise<StoredCheatProfile | null> {
    const db = await openPersistenceDatabase();
    try {
      const transaction = db.transaction(cheatProfileStoreName, 'readonly');
      const request = transaction.objectStore(cheatProfileStoreName).get(cheatProfileKey(identity));
      const result = await requestResult<StoredCheatProfile | undefined>(request);
      await transactionComplete(transaction);
      return result ?? null;
    } finally {
      db.close();
    }
  }

  async putCheatProfile(
    identity: PersistenceIdentity,
    entries: readonly CheatEntry[],
    now = new Date(),
  ): Promise<StoredCheatProfile> {
    const record: StoredCheatProfile = {
      schemaVersion: 1,
      kind: 'CHEAT_PROFILE',
      key: cheatProfileKey(identity),
      identity: { ...identity },
      entries: normalizeCheatEntries(entries),
      updatedAt: now.toISOString(),
    };

    const db = await openPersistenceDatabase();
    try {
      const transaction = db.transaction(cheatProfileStoreName, 'readwrite');
      transaction.objectStore(cheatProfileStoreName).put(record);
      await transactionComplete(transaction);
      return record;
    } finally {
      db.close();
    }
  }

  async deleteCheatProfile(identity: PersistenceIdentity): Promise<void> {
    const db = await openPersistenceDatabase();
    try {
      const transaction = db.transaction(cheatProfileStoreName, 'readwrite');
      transaction.objectStore(cheatProfileStoreName).delete(cheatProfileKey(identity));
      await transactionComplete(transaction);
    } finally {
      db.close();
    }
  }
}
